// React and hooks
import React, { useState, useEffect, useRef } from 'react';

// API client
import api from '../../lib/api';

// Icons
import { Trash, Search } from 'lucide-react';

// Theme hook
import useTheme from '../../hooks/useTheme';

// i18n
import { useTranslation } from 'react-i18next';

const AddProductWeightModal = ({
  isOpen,
  onClose,
  product,
  onUpdated,
  mode = "entry",
  notes,
  locationKey,
  clientId,
  clientName
}) => {
  // States
  const [weight, setWeight] = useState('');
  const [weights, setWeights] = useState([]);          // entry: number[] | exit: ProductWeight[]
  const [selectedIds, setSelectedIds] = useState([]);  // exit only
  const [search, setSearch] = useState('');
  const [currentInventory, setCurrentInventory] = useState(null);

  // Ref for scroll control
  const listRef = useRef(null);

  // Hooks
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Base button styles
  const baseBtn =
    theme === 'dark'
      ? "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow transition bg-white text-black hover:bg-black hover:text-white hover:scale-105 disabled:opacity-60"
      : "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow transition bg-gray-900 text-white hover:bg-white hover:text-black hover:scale-105 disabled:opacity-60";

  // Resolve product id safely
  const productId =
    product?.templateId || product?.id || product?._id || product?.productId;

  useEffect(() => {
    if (!isOpen || !productId || !locationKey) return;
    const fetchInventory = async () => {
      try {
        const { data } = await api.get(`/inventory/summary/products?location=${locationKey}`);
        const summaries = Array.isArray(data) ? data : [];
        setCurrentInventory(summaries.find((item) => item.productId === productId) || null);
      } catch (err) {
        console.error('Failed to load current product inventory', err);
        setCurrentInventory(null);
      }
    };
    fetchInventory();
  }, [isOpen, productId, locationKey]);

  // Load weights when modal opens (EXIT MODE ONLY)
  useEffect(() => {
    if (!isOpen || mode !== "exit" || !productId) return;

    const fetchWeights = async () => {
      try {
        // REAL BACKEND ENDPOINT
        const { data } = await api.get(
          `/product-weights/product/${productId}`
        );

        // Only allow NON-consumed weights
        const available = Array.isArray(data)
          ? data.filter(w => w.consumed === false)
          : [];

        setWeights(available);
      } catch (err) {
        console.error('Failed to load product weights', err);
        setWeights([]);
      }
    };

    fetchWeights();
  }, [isOpen, mode, productId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setWeight('');
      setWeights([]);
      setSelectedIds([]);
      setSearch('');
      setCurrentInventory(null);
    }
  }, [isOpen]);

  // Scroll to bottom on list update
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [weights]);

  // Add weight manually (ENTRY MODE)
  const handleAddWeight = (e) => {
    e.preventDefault();
    if (!weight || Number(weight) <= 0) return;

    const parsed = parseFloat(Number(weight).toFixed(2));
    setWeights((prev) => [...prev, parsed]);
    setWeight('');
  };

  // Delete weight by index (ENTRY MODE)
  const handleDeleteWeight = (idx) => {
    setWeights((prev) => prev.filter((_, i) => i !== idx));
  };

  // Toggle selection (EXIT MODE)
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  // Save entry or exit
  const handleSave = async () => {
    try {
      if (!productId) {
        alert(t('invalidProductId'));
        return;
      }

      // ENTRY MODE
      if (mode === "entry") {
        if (!weights.length) {
          alert(t('mustAddWeight'));
          return;
        }

        const cleanWeights = weights.map(w =>
          parseFloat(Number(w).toFixed(2))
        );

        await api.post(`/inventory/entries?location=${locationKey}`, {
          templateId: productId,
          quantity: cleanWeights.length,
          weights: cleanWeights,
          totalWeight: cleanWeights.reduce((a, b) => a + b, 0),
          notes
        });
      }

      // EXIT MODE
      else {
        if (!selectedIds.length) {
          alert(t('mustSelectBox'));
          return;
        }

        await api.post(`/inventory/exits/${productId}?location=${locationKey}`, {
          weightIds: selectedIds,
          notes,
          clientId,
          clientName
        });
      }

      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      console.error('Error saving weights:', err);
      alert(t('errorSaveWeights'));
    }
  };

  // Totals
  const totalKg =
    mode === "entry"
      ? weights.reduce((sum, w) => sum + w, 0)
      : weights
          .filter(w => selectedIds.includes(w.id || w._id))
          .reduce((sum, w) => sum + (w.weight || 0), 0);

  const totalBoxes =
    mode === "entry" ? weights.length : selectedIds.length;

  // Filter weights
  const filteredWeights =
    mode === "entry"
      ? weights.filter(w => w.toString().includes(search))
      : weights.filter(w =>
          w.weight?.toString().includes(search)
        );

  if (!isOpen || !product) return null;

  // Render
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-sm shadow-lg space-y-4">
        {/* Header */}
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">
          {mode === "entry" ? t('addWeightsFor') : t('selectWeightsFor')} {product.name}
        </h2>

        {/* ENTRY MODE */}
        {mode === "entry" && (
          <form onSubmit={handleAddWeight} className="flex gap-2">
            <input
              type="number"
              step="0.01"
              placeholder={t('enterWeight')}
              className="flex-1 border rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              required
            />
            <button type="submit" className={baseBtn}>{t('add')}</button>
          </form>
        )}

        {/* EXIT MODE SEARCH */}
        {mode === "exit" && (
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
            <Search size={16} className="text-gray-500 dark:text-gray-300 mr-2" />
            <input
              type="text"
              placeholder={t('searchWeight')}
              className="bg-transparent outline-none flex-1 text-sm text-gray-700 dark:text-gray-200"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}

        {/* Weights list */}
        {filteredWeights.length > 0 ? (
          <div ref={listRef} className="space-y-2 max-h-96 overflow-y-auto border-t pt-2">
            {mode === "entry"
              ? filteredWeights.map((w, i) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span>{w.toFixed(2)} KG</span>
                    <button
                      onClick={() => handleDeleteWeight(i)}
                      className="p-1 rounded bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                ))
              : filteredWeights.map((w) => {
                  const weightId = w.id || w._id;
                  return (
                    <label
                      key={weightId}
                      className={`flex justify-between items-center text-sm px-2 py-1 rounded cursor-pointer ${
                        selectedIds.includes(weightId)
                          ? "bg-rose-600 text-white"
                          : "hover:bg-gray-200 dark:hover:bg-gray-700"
                      }`}
                      onClick={() => toggleSelect(weightId)}
                    >
                      <span>{(w.weight || 0).toFixed(2)} KG</span>
                    </label>
                  );
                })}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            {mode === "entry" ? t('noWeightsYet') : t('noAvailableBoxes')}
          </p>
        )}

        {/* Totals */}
        <div className="border-t pt-2 text-sm text-gray-700 dark:text-gray-200">
          <p>{t('totalBoxes')}: {totalBoxes}</p>
          <p>{t('totalKg')}: {totalKg.toFixed(2)}</p>
          <p>{t('availableNow')}: {Number(currentInventory?.totalBoxes || 0)} {t('boxes')} · {Number(currentInventory?.totalWeight || 0).toFixed(2)} KG</p>
          <p className="font-semibold">
            {mode === 'entry' ? t('projectedAfterEntry') : t('projectedAfterExit')}: {' '}
            {Math.max(0, Number(currentInventory?.totalBoxes || 0) + (mode === 'entry' ? totalBoxes : -totalBoxes))} {t('boxes')} · {' '}
            {Math.max(0, Number(currentInventory?.totalWeight || 0) + (mode === 'entry' ? totalKg : -totalKg)).toFixed(2)} KG
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className={baseBtn}>{t('cancel')}</button>
          <button onClick={handleSave} className={baseBtn}>{t('save')}</button>
        </div>
      </div>
    </div>
  );
};

export default AddProductWeightModal;
